/**
 * @file interpret/interpretUi.ts
 * @brief Pure display rules for the interpretation screen (labels, timer, RTT).
 *
 * @details
 * Status wording and priority are the phone's (`SileroConversationScreen`
 * F:2347-2371): ending > reconnecting > paused > translating > playing >
 * speaking/listening > starting. "Paused" is shown muted, like the phone's grey.
 *
 * @author DES
 * @date 01-10-2026
 */

import type { InterpretPhase } from "./engine/interpretController";
import type { S2sConnection } from "./engine/s2sSocket";

export type StatusIcon =
  | "hourglass"
  | "cloud-off"
  | "hand"
  | "languages"
  | "volume"
  | "mic"
  | "loader";

export type StatusView = { label: string; icon: StatusIcon; muted: boolean };

export function statusView(
  phase: InterpretPhase,
  connection: S2sConnection,
  speaking: boolean,
): StatusView | null {
  if (phase === "ending") return { label: "Ending session…", icon: "hourglass", muted: false };
  if (connection === "reconnecting" && phase !== "starting") {
    return { label: "Reconnecting…", icon: "cloud-off", muted: false };
  }
  switch (phase) {
    case "paused":
      return { label: "Paused", icon: "hand", muted: true };
    case "translating":
      return { label: "Translating…", icon: "languages", muted: false };
    case "playing":
      return { label: "Playing translation", icon: "volume", muted: false };
    case "listening":
      return speaking
        ? { label: "Speaking", icon: "mic", muted: false }
        : { label: "Listening", icon: "mic", muted: false };
    case "starting":
      return { label: "Starting…", icon: "loader", muted: false };
    default:
      return null;
  }
}

/** The phone's `formatHMS`: 00:00:00. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

/** The phone's RTT pill: "1.2s" from a second up, else "840ms". */
export function rttLabel(ms: number | undefined | null): string | null {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

/**
 * "Speaking" vs "Listening" from the mic level, with hysteresis so the label
 * doesn't flicker at the threshold (the phone flips a boolean the same way).
 */
export function nextSpeaking(prev: boolean, rms: number): boolean {
  return prev ? rms > 0.02 : rms > 0.035;
}
